import React, { useState } from 'react';
import { useStore } from '../context/StoreContext';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import CommunicationModal from '../components/CommunicationModal';
import TemplateModal from '../components/TemplateModal';
import ScheduleModal from '../components/ScheduleModal';
import TemplatePreviewModal from '../components/TemplatePreviewModal';
import { format, parseISO } from 'date-fns';

const { FiPlus, FiSearch, FiMail, FiPhone, FiMessageCircle, FiFileText, FiTrash2, FiClock, FiEdit2, FiZap, FiCheckCircle, FiCopy, FiFilter, FiEye } = FiIcons;

const Communications = () => {
  const { data, addRecord, updateRecord, deleteRecord } = useStore();
  const [activeTab, setActiveTab] = useState('templates');
  const [isLogModalOpen, setIsLogModalOpen] = useState(false);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [selectedPreview, setSelectedPreview] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  const filteredHistory = (data.communications || [])
    .filter(c => c.subject.toLowerCase().includes(searchTerm.toLowerCase()) || c.tenantName.toLowerCase().includes(searchTerm.toLowerCase()));

  const filteredTemplates = (data.communicationTemplates || [])
    .filter(t => {
      const matchesSearch = t.title.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCat = categoryFilter === 'all' || t.category === categoryFilter;
      return matchesSearch && matchesCat;
    });

  const categories = ['all', 'General', 'Onboarding', 'Lease', 'Maintenance'];

  const handlePreview = (template) => {
    // Pick first occupied property for preview context
    const demoProperty = data.properties.find(p => p.status === 'Occupied') || data.properties[0];
    setSelectedPreview({ template, property: demoProperty });
    setIsPreviewModalOpen(true);
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500 pb-12">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-black text-gray-800 tracking-tight">Communication Hub</h2>
          <p className="text-sm text-gray-500 font-medium italic">Standardizing guest interactions with blueprints.</p>
        </div>
        <div className="flex gap-2">
          {activeTab === 'templates' ? (
            <button 
              onClick={() => { setEditingTemplate(null); setIsTemplateModalOpen(true); }}
              className="flex items-center px-5 py-3 bg-purple-600 text-white rounded-2xl hover:bg-purple-700 transition shadow-lg shadow-purple-100 font-black text-sm uppercase tracking-wider"
            >
              <SafeIcon icon={FiPlus} className="mr-2" /> Create Blueprint
            </button>
          ) : (
            <button 
              onClick={() => setIsLogModalOpen(true)}
              className="flex items-center px-5 py-3 bg-indigo-600 text-white rounded-2xl hover:bg-indigo-700 transition shadow-lg shadow-indigo-100 font-black text-sm uppercase tracking-wider"
            >
              <SafeIcon icon={FiPlus} className="mr-2" /> Log Touchpoint
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-4 rounded-3xl shadow-sm border border-gray-100">
        <div className="flex bg-gray-50 p-1 rounded-2xl overflow-x-auto">
          {[
            { id: 'history', label: 'Audit Trail', icon: FiClock },
            { id: 'templates', label: 'Blueprint Library', icon: FiFileText },
            { id: 'scheduled', label: 'Automations', icon: FiZap }
          ].map(tab => (
            <button 
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center px-6 py-2 text-xs font-black uppercase tracking-tight rounded-xl transition-all whitespace-nowrap ${activeTab === tab.id ? 'bg-white text-indigo-600 shadow-sm' : 'text-gray-400 hover:text-gray-600'}`}
            >
              <SafeIcon icon={tab.icon} className="mr-2" /> {tab.label}
            </button>
          ))}
        </div>
        <div className="relative flex-1 max-w-md">
          <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
          <input 
            type="text" 
            placeholder="Search templates or history..." 
            className="w-full pl-12 pr-4 py-2.5 bg-gray-50 border-none rounded-2xl outline-none focus:ring-2 focus:ring-indigo-500 text-sm font-medium"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {activeTab === 'templates' && (
        <div className="space-y-8">
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {categories.map(cat => (
              <button 
                key={cat}
                onClick={() => setCategoryFilter(cat)}
                className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all border whitespace-nowrap ${categoryFilter === cat ? 'bg-indigo-600 text-white border-indigo-600 shadow-md shadow-indigo-100' : 'bg-white text-gray-400 border-gray-100 hover:border-indigo-200'}`}
              >
                {cat}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredTemplates.map(t => (
              <div key={t.id} className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm relative group hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <div className="flex justify-between items-start mb-6">
                  <div className={`p-4 rounded-2xl ${
                    t.category === 'Onboarding' ? 'bg-green-50 text-green-600' : 
                    t.category === 'Lease' ? 'bg-blue-50 text-blue-600' : 
                    t.category === 'Maintenance' ? 'bg-amber-50 text-amber-600' : 
                    'bg-purple-50 text-purple-600'
                  }`}>
                    <SafeIcon icon={FiFileText} size={24} />
                  </div>
                  <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button 
                      onClick={() => handlePreview(t)}
                      className="p-2 text-gray-400 hover:text-indigo-600 transition bg-gray-50 rounded-xl"
                      title="Quick Preview"
                    >
                      <SafeIcon icon={FiEye} size={16} />
                    </button>
                    <button 
                      onClick={() => { setEditingTemplate(t); setIsTemplateModalOpen(true); }}
                      className="p-2 text-gray-400 hover:text-indigo-600 transition bg-gray-50 rounded-xl"
                      title="Edit"
                    >
                      <SafeIcon icon={FiEdit2} size={16} />
                    </button>
                  </div>
                </div>

                <div>
                  <h4 className="text-lg font-black text-gray-900 mb-1 leading-tight">{t.title}</h4>
                  <p className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-4">{t.category}</p>
                  
                  <div className="bg-gray-50/50 rounded-2xl p-4 border border-gray-50 overflow-hidden">
                    <p className="text-xs text-gray-600 font-medium line-clamp-3 leading-relaxed italic">
                      {t.content}
                    </p>
                  </div>
                  
                  <div className="mt-6 flex items-center justify-between">
                    <div className="flex items-center gap-1 text-[9px] font-black text-indigo-500 uppercase tracking-tighter">
                      <SafeIcon icon={FiZap} size={10} /> {t.content.match(/\[(.*?)\]/g)?.length || 0} Dynamic Tags
                    </div>
                    <button 
                      onClick={() => addRecord('communicationTemplates', { ...t, title: `${t.title} (Copy)` })}
                      className="text-[10px] font-black text-gray-400 uppercase tracking-widest hover:text-indigo-600 transition"
                    >
                      Duplicate
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'history' && (
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden divide-y divide-gray-50 animate-in slide-in-from-bottom-4 duration-500">
          {filteredHistory.length === 0 ? (
            <div className="py-24 text-center">
              <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiClock} size={32} className="text-gray-200" />
              </div>
              <p className="text-xs font-black text-gray-400 uppercase tracking-widest">No interactions logged yet</p>
            </div>
          ) : (
            filteredHistory.map((item) => (
              <div key={item.id} className="p-6 hover:bg-gray-50/50 transition-colors group">
                <div className="flex gap-4">
                  <div className="w-12 h-12 bg-indigo-50 text-indigo-600 rounded-2xl flex items-center justify-center shrink-0 shadow-sm">
                    <SafeIcon icon={item.type === 'Call' ? FiPhone : item.type === 'SMS' ? FiMessageSquare : FiMail} size={20} />
                  </div>
                  <div className="flex-1">
                    <div className="flex justify-between mb-1">
                      <h4 className="font-black text-gray-900">{item.subject}</h4>
                      <span className="text-[10px] font-black text-gray-400 uppercase">{item.date}</span>
                    </div>
                    <p className="text-sm text-indigo-600 font-black mb-2">{item.tenantName}</p>
                    <p className="text-sm text-gray-600 italic line-clamp-2 leading-relaxed font-medium">"{item.notes}"</p>
                  </div>
                  <button 
                    onClick={() => deleteRecord('communications', item.id)}
                    className="p-2 text-gray-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity self-start"
                  >
                    <SafeIcon icon={FiTrash2} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      <CommunicationModal 
        isOpen={isLogModalOpen} 
        onClose={() => setIsLogModalOpen(false)} 
        onSave={(entry) => addRecord('communications', entry)} 
        properties={data.properties} 
      />
      <TemplateModal 
        isOpen={isTemplateModalOpen} 
        onClose={() => setIsTemplateModalOpen(false)} 
        onSave={(template) => editingTemplate ? updateRecord('communicationTemplates', editingTemplate.id, template) : addRecord('communicationTemplates', template)} 
        initialData={editingTemplate} 
      />
      <ScheduleModal 
        isOpen={isScheduleModalOpen} 
        onClose={() => setIsScheduleModalOpen(false)} 
        onSave={(sc) => addRecord('scheduledCommunications', sc)} 
        properties={data.properties} 
      />
      <TemplatePreviewModal 
        isOpen={isPreviewModalOpen}
        onClose={() => { setIsPreviewModalOpen(false); setSelectedPreview(null); }}
        template={selectedPreview?.template}
        property={selectedPreview?.property}
      />
    </div>
  );
};

export default Communications;